import { useState, useEffect, useCallback } from 'react';
import { DotationsState } from './Droits.types';
import { DroitsActions } from './Droits.actions';
import { useSessionStore } from '../../lib/Store/sessionStore.component';
import { usePolling } from '../../hooks/usePolling/usePolling.hook';
import { useNotify } from '../../hooks/useNotify/useNotify';

export const useDroitsState = () => {
  const {currentLogin} = useSessionStore.getState().getCurrentInfos();
  const notify = useNotify();

  const [dotations, setDotations] = useState<DotationsState>({
    CP: null,
    RTT: null,
    HS: null,
  });
  

  const [error, setError] = useState<string | null>(null);
  
  /**
   * Charge toutes les dotations
   */
  const loadDotations = useCallback(async () => {
    if (!currentLogin?.identifiant) return

    setError(null);

    try {     
      const fetchedDotations = await DroitsActions.fetchAllDotations({
        identifiantAgent: currentLogin?.identifiant.toString() || '',
        identifiantEntite: currentLogin?.identifiantEntiteDefautEsag.toString() || ''
      });

      setDotations(fetchedDotations);      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors du chargement des dotations';
      console.error('[useDroitsState] Error loading dotations:', err);
      setError(errorMessage);
    } finally {
    }
  }, [currentLogin?.identifiant, currentLogin?.identifiantEntiteDefautEsag]);

  /**
   * Recharge les dotations
   */
  const refreshDotations = () => {
    loadDotations();
  };

  // Chargement initial des dotations
  useEffect(() => {
    loadDotations();
  }, [loadDotations]);

  // Polling silencieux pour détecter les changements de soldes
  usePolling<DotationsState>({
    fetchFn: async () => {
      if (!currentLogin?.identifiant) return null
      return DroitsActions.fetchAllDotations({
        identifiantAgent: currentLogin.identifiant.toString(),
        identifiantEntite: currentLogin.identifiantEntiteDefautEsag.toString()
      }, true)
    },
    enabled: !!currentLogin?.identifiant,
    hasChanged: (prev, next) => {
      // Comparer uniquement les valeurs clés des dotations
      const compare = (a: any, b: any) => {
        if (!a && !b) return false
        if (!a || !b) return true
        return a.droit !== b.droit || a.reste !== b.reste || a.pris_a_valider !== b.pris_a_valider || a.pris_a_venir !== b.pris_a_venir
      }
      return compare(prev.CP, next.CP) || compare(prev.RTT, next.RTT) || compare(prev.HS, next.HS)
    },
    onChanged: (next) => {
      setDotations(next)
      notify.info('Vos soldes ont été mis à jour', 3000)
    },
  })

  return {
    // État
    dotations,
    error,
    
    // Actions
    refreshDotations,
    
    // Données dérivées
    hasCP: !!dotations.CP,
    hasRTT: !!dotations.RTT,
    hasHS: !!dotations.HS,
    
    // Indicateurs de disponibilité des données
    hasData: !!(dotations.CP || dotations.RTT || dotations.HS),
  };
};